import {
	MutableRefObject,
	PropsWithChildren,
	ReactNode,
	Ref,
	createContext,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Breakpoint, printUnclip } from '../helpers';
import { NoPrintWrapper } from './NoPrintWrapper';

const NAV_WIDTH = 240;
const ASIDE_WIDTH = 360;

const Page = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	height: 100dvh;
	width: 100%;
	min-width: 0;
	overflow: hidden;
	background: ${(props) => props.theme.tokens.surface.secondary};

	${printUnclip}

	@media print {
		background: ${(props) => props.theme.tokens.surface.white};
	}
`;

const Body = styled(Flex)`
	align-items: stretch;
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;
	width: 100%;

	${printUnclip}
`;

const Nav = styled(Box)`
	box-sizing: border-box;
	flex-direction: column;
	align-self: stretch;
	min-height: 0;
	overflow: hidden;

	@media print {
		display: none !important;
	}
`;

const MainColumn = styled(Box)`
	flex-direction: column;
	flex: 1 1 0%;
	min-width: 0;
	min-height: 0;

	${printUnclip}
`;

const ContentRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: stretch;
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;

	${printUnclip}
`;

const Dock = styled.div<{ $present: boolean }>`
	display: ${(props) => (props.$present ? 'flex' : 'none')};
	flex-direction: row;
	align-items: stretch;
	flex: 0 0 auto;
	min-width: 0;
	min-height: 0;
	gap: ${(props) => props.theme.tokens.gap.element}px;
	margin: 0 ${(props) => props.theme.tokens.gap.element}px
		${(props) => props.theme.tokens.gap.element}px 0;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		display: none;
	}

	@media print {
		display: none !important;
	}
`;

const Aside = styled.aside<{ $open: boolean }>`
	box-sizing: border-box;
	display: ${(props) => (props.$open ? 'flex' : 'none')};
	flex-direction: column;
	flex: 0 0 ${ASIDE_WIDTH}px;
	width: ${ASIDE_WIDTH}px;
	min-width: 0;
	min-height: 0;
	overflow: hidden;
	background: ${(props) => props.theme.tokens.surface.white};
	border-radius: ${(props) => props.theme.tokens.radius.xl}px;
	border: 1px solid ${(props) => props.theme.tokens.border.subtle};
`;

const Rail = styled.div`
	display: flex;
	flex-direction: column;
	align-self: flex-start;
	flex: 0 0 auto;
`;

const Main = styled.main`
	display: flex;
	flex-direction: column;
	flex-shrink: 1;
	flex-grow: 1;
	min-width: 0;
	min-height: 0;
	background: ${(props) => props.theme.tokens.surface.white};
	border-radius: ${(props) => props.theme.tokens.radius.xl}px;
	margin: 0 ${(props) => props.theme.tokens.gap.element}px
		${(props) => props.theme.tokens.gap.element}px 0;
	overflow: auto;
	border: 1px solid ${(props) => props.theme.tokens.border.subtle};

	@media screen and (max-width: ${Breakpoint.Medium}) {
		margin: 0;
		border-radius: 0;
		border: none;
	}

	${printUnclip}

	@media print {
		border-radius: 0;
		margin: 0;
		border: none;
	}
`;

const AppScrollContext = createContext<HTMLElement | null>(null);
const AppAsideSlotContext = createContext<HTMLElement | null>(null);
const AppToolSlotContext = createContext<HTMLElement | null>(null);
const AppDockPresenceContext = createContext<(present: boolean) => void>(() => {});

type AppAsideControl = {
	open: boolean;
	setOpen: (open: boolean) => void;
};

const AppAsideControlContext = createContext<AppAsideControl>({
	open: false,
	setOpen: () => {},
});

/**
 * The document itself does not scroll under this layout, so anything that wants
 * to read or drive the scroll position has to go through the main element.
 */
export function useAppScrollElement() {
	return useContext(AppScrollContext);
}

export function useAppAsideControl() {
	return useContext(AppAsideControlContext);
}

export function AppAside(props: PropsWithChildren) {
	const slot = useContext(AppAsideSlotContext);

	if (!slot) {
		return null;
	}

	return createPortal(props.children, slot);
}

export function AppTool(props: PropsWithChildren) {
	const slot = useContext(AppToolSlotContext);
	const setPresent = useContext(AppDockPresenceContext);

	useLayoutEffect(() => {
		setPresent(true);
		return () => setPresent(false);
	}, [setPresent]);

	if (!slot) {
		return null;
	}

	return createPortal(props.children, slot);
}

export function AppLayout(
	props: PropsWithChildren<{
		banner?: ReactNode;
		nav: ReactNode;
		header?: ReactNode;
		navOpen?: boolean;
		mainRef?: Ref<HTMLElement>;
	}>,
) {
	const navOpen = props.navOpen ?? false;
	const { mainRef } = props;
	const [main, setMain] = useState<HTMLElement | null>(null);
	const [asideSlot, setAsideSlot] = useState<HTMLElement | null>(null);
	const [toolSlot, setToolSlot] = useState<HTMLElement | null>(null);
	const [asideOpen, setAsideOpen] = useState(false);
	const [dockPresent, setDockPresent] = useState(false);
	const setOpen = useCallback((open: boolean) => setAsideOpen(open), []);
	const setDock = useCallback((present: boolean) => setDockPresent(present), []);
	const asideControl = useMemo(() => ({ open: asideOpen, setOpen }), [asideOpen, setOpen]);

	const attachMain = useCallback(
		(element: HTMLElement | null) => {
			setMain(element);

			if (typeof mainRef === 'function') {
				mainRef(element);
			} else if (mainRef) {
				(mainRef as MutableRefObject<HTMLElement | null>).current = element;
			}
		},
		[mainRef],
	);

	return (
		<AppScrollContext.Provider value={main}>
			<AppAsideControlContext.Provider value={asideControl}>
				<AppAsideSlotContext.Provider value={asideSlot}>
					<AppToolSlotContext.Provider value={toolSlot}>
						<AppDockPresenceContext.Provider value={setDock}>
							<Page>
								{props.banner ? <NoPrintWrapper>{props.banner}</NoPrintWrapper> : null}
								<Body flexDirection={['column', 'column', 'row']}>
									<Nav
										display={[navOpen ? 'flex' : 'none', navOpen ? 'flex' : 'none', 'flex']}
										flex={['1 1 auto', '1 1 auto', `0 0 ${NAV_WIDTH}px`]}
										width={['100%', '100%', NAV_WIDTH]}
									>
										{props.nav}
									</Nav>
									<MainColumn
										display={[navOpen ? 'none' : 'flex', navOpen ? 'none' : 'flex', 'flex']}
									>
										{props.header ? <NoPrintWrapper>{props.header}</NoPrintWrapper> : null}
										<ContentRow>
											<Main ref={attachMain}>{props.children}</Main>
											<Dock $present={dockPresent || asideOpen}>
												<Aside ref={setAsideSlot} $open={asideOpen} />
												<Rail ref={setToolSlot} />
											</Dock>
										</ContentRow>
									</MainColumn>
								</Body>
							</Page>
						</AppDockPresenceContext.Provider>
					</AppToolSlotContext.Provider>
				</AppAsideSlotContext.Provider>
			</AppAsideControlContext.Provider>
		</AppScrollContext.Provider>
	);
}
